import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ParabolaLogo from '../components/ParabolaLogo';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { login as loginApi, fetchProfile } from '../api/auth';
import useAuthStore from '../store/authStore';
import { ROLES } from '../utils/roles';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

const roleRedirect = {
  [ROLES.ADMIN]: '/admin',
  [ROLES.TUTOR]: '/tutor',
  [ROLES.PARENT]: '/parent',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: '#fff !important',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiOutlinedInput-input': { backgroundColor: 'transparent !important' },
  '& .MuiInputAdornment-root': { backgroundColor: 'transparent' },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px #fff inset !important',
    WebkitTextFillColor: '#1E2A4A !important',
  },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser, logout } = useAuthStore();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { email: '', password: '' },
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: loginApi,
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const authData = await mutateAsync(values);
      login({ token: authData.token, role: authData.role });
      const profile = await fetchProfile();
      setUser(profile);
      const redirectTo = roleRedirect[authData.role] || '/login';
      const fromState = location.state?.from?.pathname;
      navigate(fromState || redirectTo, { replace: true });
    } catch (err) {
      logout();
      const message = err.response?.data?.message || 'Не вдалося увійти. Перевірте дані.';
      setError(message);
    }
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #FFFFFF 0%, #EDFEE5 40%, #D3FEC6 100%)',
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 400,
          width: '100%',
          borderRadius: 5,
          bgcolor: '#fff',
          boxShadow: '0 16px 48px rgba(67,87,173,0.12)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', pt: { xs: 3, sm: 5 }, pb: 1, px: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ParabolaLogo width={160} height={56} style={{ color: B.dark }} />
          </Box>
          <Typography variant="body2" sx={{ color: '#8891A5', mt: 0.5 }}>
            Привіт! Увійди, щоб продовжити
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: { xs: 3, sm: 5 }, pt: 3 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2.5, borderRadius: 3 }}
            >
              {error}
            </Alert>
          )}

          <Stack spacing={2.5} component="form" onSubmit={onSubmit}>
            <TextField
              label="Email"
              type="email"
              {...register('email', { required: 'Введіть email' })}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ fontSize: 20, color: '#C4B5FD' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Пароль"
              type={showPassword ? 'text' : 'password'}
              {...register('password', { required: 'Введіть пароль' })}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyOutlinedIcon sx={{ fontSize: 20, color: '#C4B5FD' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      sx={{ color: '#8891A5' }}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isPending}
              fullWidth
              sx={{
                bgcolor: B.primary,
                '&:hover': { bgcolor: '#3648A0' },
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 16,
                py: 1.5,
                boxShadow: '0 4px 14px rgba(67,87,173,0.35)',
              }}
            >
              {isPending ? 'Входжу...' : 'Увійти'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
